import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { wrapText } from '../utils/helpers';

export const generateRCAPDF = async (formData) => {
  // Load the template PDF
  const templateUrl = '/RCATemplate/RGA_Head.pdf';
  const existingPdfBytes = await fetch(templateUrl).then(res => res.arrayBuffer());
  const pdfDoc = await PDFDocument.load(existingPdfBytes);
  
  // Get first page and embed font
  const pages = pdfDoc.getPages();
  let currentPage = pages[0];
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  // Page dimensions
  const { height, width } = currentPage.getSize();
  const fontSize = 11;
  const lineHeight = 15;
  const marginBottom = 50; // Bottom margin to trigger new page
  const pageStartY = 50; // Top margin for new pages

  // Helper function to check if we need a new page
  const checkNewPage = () => {
    if (yPosition > height - marginBottom) {
      currentPage = pdfDoc.addPage([width, height]);
      yPosition = pageStartY;
      return true;
    }
    return false;
  };

  // Helper function to draw text on current page
  const drawText = (text, x, y, options = {}) => {
    currentPage.drawText(text || '', {
      x,
      y: height - y,
      size: options.size || fontSize,
      font: options.bold ? boldFont : font,
      color: rgb(0, 0, 0),
      ...options,
    });
  };

  // Fill in the form fields
  let yPosition = 120;

  // Title and Date
  drawText('Root Cause Analysis', 50, yPosition, { bold: true, size: 16 });
  drawText(`Date: ${formData.reportDate}`, 400, yPosition, { size: 11 });
  yPosition += lineHeight * 3;

  // Fault Description (multi-line)
  drawText('Fault Description:', 50, yPosition, { bold: true });
  yPosition += lineHeight;
  const faultLines = wrapText(formData.fault, 90);
  faultLines.forEach(line => {
    checkNewPage();
    drawText(line, 50, yPosition);
    yPosition += lineHeight;
  });
  yPosition += lineHeight;

  checkNewPage();
  drawText('Location Affected:', 50, yPosition, { bold: true });
  drawText(formData.locationAffected, 180, yPosition);
  yPosition += lineHeight * 2;

  checkNewPage();
  drawText('Date of Fault:', 50, yPosition, { bold: true });
  drawText(formData.faultDate, 150, yPosition);
  yPosition += lineHeight;

  checkNewPage();
  drawText('Time of Fault:', 50, yPosition, { bold: true });
  drawText(formData.faultTime, 150, yPosition);
  yPosition += lineHeight * 1.5;

  checkNewPage();
  drawText('Severity Level:', 50, yPosition, { bold: true });
  drawText(formData.severityLevel, 150, yPosition);
  yPosition += lineHeight * 2;

  // Impact Assessment Section
  checkNewPage();
  drawText('Impact Assessment', 50, yPosition, { bold: true, size: 13 });
  yPosition += lineHeight * 1.5;

  checkNewPage();
  drawText('Safety Impact:', 50, yPosition, { bold: true });
  drawText(formData.safetyImpact, 150, yPosition);
  yPosition += lineHeight;

  checkNewPage();
  drawText('Operational Impact:', 50, yPosition, { bold: true });
  drawText(formData.operationalImpact, 190, yPosition);
  yPosition += lineHeight;

  checkNewPage();
  drawText('Service Impact Duration:', 50, yPosition, { bold: true });
  drawText(`${formData.serviceImpactDuration} minutes`, 210, yPosition);
  yPosition += lineHeight;

  checkNewPage();
  drawText('Stakeholders Affected:', 50, yPosition, { bold: true });
  yPosition += lineHeight;
  const stakeholdersLines = wrapText(formData.stakeholdersAffected, 90);
  stakeholdersLines.forEach(line => {
    checkNewPage();
    drawText(line, 50, yPosition);
    yPosition += lineHeight;
  });
  yPosition += lineHeight;

  // System Behavior Section
  checkNewPage();
  drawText('System Behavior', 50, yPosition, { bold: true, size: 13 });
  yPosition += lineHeight * 1.5;

  checkNewPage();
  drawText('Operational Characteristics:', 50, yPosition, { bold: true });
  yPosition += lineHeight;
  const operationalCharacteristicsLines = wrapText(formData.operationalCharacteristics, 90);
  operationalCharacteristicsLines.forEach(line => {
    checkNewPage();
    drawText(line, 50, yPosition);
    yPosition += lineHeight;
  });
  yPosition += lineHeight;

  checkNewPage();
  drawText('Supervisory State:', 50, yPosition, { bold: true });
  yPosition += lineHeight;
  const supervisoryStateLines = wrapText(formData.supervisoryState, 90);
  supervisoryStateLines.forEach(line => {
    checkNewPage();
    drawText(line, 50, yPosition);
    yPosition += lineHeight;
  });
  yPosition += lineHeight;

  // Fault log content (if uploaded)
  if (formData.faultLog) {
    const logContent = await formData.faultLog.text();
    checkNewPage();
    drawText('Fault Log:', 50, yPosition, { bold: true });
    yPosition += lineHeight;
    
    // Split log content into lines and add to PDF
    const logLines = logContent.split('\n');
    logLines.forEach(line => {
      checkNewPage();
      drawText(line.substring(0, 80), 50, yPosition, { size: 9 });
      yPosition += 12;
    });
    yPosition += lineHeight;
  }

  checkNewPage();
  drawText('Conclusion:', 50, yPosition, { bold: true });
  yPosition += lineHeight;
  
  // Wrap conclusion text
  const conclusionLines = wrapText(formData.conclusion, 90);
  conclusionLines.forEach(line => {
    checkNewPage();
    drawText(line, 50, yPosition);
    yPosition += lineHeight;
  });
  yPosition += lineHeight;

  checkNewPage();
  drawText('Root Cause Validation:', 50, yPosition, { bold: true });
  yPosition += lineHeight;
  
  // Wrap root cause validation text
  const validationLines = wrapText(formData.rootCauseValidation, 90);
  validationLines.forEach(line => {
    checkNewPage();
    drawText(line, 50, yPosition);
    yPosition += lineHeight;
  });
  yPosition += lineHeight;

  checkNewPage();
  drawText('Recommendations:', 50, yPosition, { bold: true });
  yPosition += lineHeight;
  
  // Wrap recommendations text
  const recommendationsLines = wrapText(formData.recommendations, 90);
  recommendationsLines.forEach(line => {
    checkNewPage();
    drawText(line, 50, yPosition);
    yPosition += lineHeight;
  });
  yPosition += lineHeight;

  // Embed images if uploaded
  if (formData.images.length > 0) {
    yPosition += lineHeight * 2; // Add spacing before images
    
    for (let i = 0; i < formData.images.length; i++) {
      // formData.images items are { file, caption }
      const imageEntry = formData.images[i];
      const imageFile = imageEntry.file;
      const captionText = imageEntry.caption || '';
      const imageBytes = await imageFile.arrayBuffer();
      
      let image;
      try {
        if (imageFile.type === 'image/png') {
          image = await pdfDoc.embedPng(imageBytes);
        } else if (imageFile.type === 'image/jpeg' || imageFile.type === 'image/jpg') {
          image = await pdfDoc.embedJpg(imageBytes);
        } else {
          // Try to embed as JPG for other formats
          image = await pdfDoc.embedJpg(imageBytes);
        }
      } catch (error) {
        console.error('Error embedding image:', error);
        continue; // Skip this image and move to next
      }

      // Start each image on its own page
      currentPage = pdfDoc.addPage([width, height]);
      yPosition = pageStartY;

      // Calculate available space for image (leave margins and space for caption)
      const maxWidth = width - 100; // 50px margin each side
      const maxHeight = height - pageStartY - marginBottom - 80; // reserve space for caption and label
      const scale = Math.min(maxWidth / image.width, maxHeight / image.height, 1);
      const imageDims = image.scale(scale);

      // Draw optional label above image
      drawText(`Image ${i + 1}:`, 50, yPosition, { bold: true });
      yPosition += lineHeight;

      // Center image horizontally
      const imageX = (width - imageDims.width) / 2;
      const imageY = height - yPosition - imageDims.height;

      currentPage.drawImage(image, {
        x: imageX,
        y: imageY,
        width: imageDims.width,
        height: imageDims.height,
      });

      // Draw caption centered under the image
      const captionSize = 10;
      const captionY = height - (yPosition + imageDims.height) - 20; // 20px below image
      const captionWidth = font.widthOfTextAtSize(captionText, captionSize);
      const captionX = Math.max(50, (width - captionWidth) / 2);
      if (captionText) {
        currentPage.drawText(captionText, {
          x: captionX,
          y: captionY,
          size: captionSize,
          font,
          color: rgb(0,0,0),
        });
      }
    }
  }

  // Save and return PDF bytes
  const pdfBytes = await pdfDoc.save();
  return pdfBytes;
};
